import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('amirPizzaCart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [orders, setOrders] = useState(() => {
    const savedOrders = localStorage.getItem('amirPizzaOrders');
    return savedOrders ? JSON.parse(savedOrders) : [];
  });

  useEffect(() => {
    localStorage.setItem('amirPizzaCart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('amirPizzaOrders', JSON.stringify(orders));
  }, [orders]);

  const addToCart = (item) => {
    setCart(prev => [...prev, { ...item, id: Date.now() + Math.random() }]);
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const updateQuantity = (id, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQuantity = (item.quantity || 1) + delta;
        if (newQuantity <= 0) return null;
        return { ...item, quantity: newQuantity };
      }
      return item;
    }).filter(Boolean));
  };

  const updateSize = (id, size, price) => {
    setCart(prev => prev.map(item =>
      item.id === id ? { ...item, selectedSize: size, price: price } : item
    ));
  };

  const clearCart = () => {
    setCart([]);
  };

  const checkout = async (location, deliveryType = 'Lieferung', deliveryFee = 0, name = '', paymentMethod = 'cash', visibleItems = null) => {
    if (deliveryType === 'Lieferung' && (!location || !location.trim())) return;
    const itemsForOrder = Array.isArray(visibleItems) ? visibleItems : cart;
    const subtotal = itemsForOrder.reduce((total, item) => {
      const quantity = item.quantity || 1;
      let itemPrice = item.price || 0;

      // Use item.prices.sonder existence as the pizza check (category is stored as a label, not a key)
      if (!item.dealId && item.selectedSizeKey === 'normal' && deliveryType === 'Abholung') {
        if (item.prices && item.prices.sonder !== undefined) {
          itemPrice = item.prices.sonder;
        }
      }

      const extrasTotal = (item.selectedExtras || []).reduce((sum, extra) => sum + (extra.price || 0), 0);
      return total + (itemPrice + extrasTotal) * quantity;
    }, 0);
    const total = subtotal + deliveryFee;

    const order = {
      id: Date.now(),
      items: itemsForOrder,
      date: new Date().toISOString(),
      subtotal,
      deliveryFee,
      deliveryType,
      total,
      location: location.trim(),
      name: name.trim(),
      paymentMethod,
    };
    setOrders(prev => [...prev, order]);

    // Send order to backend webhook
    const apiUrl = process.env.NODE_ENV === 'production' ? 'https://amirspizza-backend.vercel.app' : (process.env.REACT_APP_API_URL || 'http://localhost:3001');
    try {
      await fetch(`${apiUrl}/api/orders/webhook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...order,
          phone: '', // Optional: update CartPage to collect phone number if needed later
          email: ''
        })
      });
    } catch (error) {
      console.error("Failed to send order to backend:", error);
    }

    const message = generateWhatsAppMessage(itemsForOrder, location.trim(), deliveryType, deliveryFee, name.trim(), paymentMethod);
    const whatsappNumber = '4915206127270';
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.location.href = url;

    clearCart();
  };

  const calculateTotal = () => {
    const pizzaCats = ['pizzaKlassiker', 'donerPizza', 'mitKaserand', 'mitFischbelag', 'vegetarischePizza'];
    const priceMap = { 1: 15.00, 2: 30.00 };
    const dealCounts = {};
    let total = 0;
    cart.forEach(item => {
      const quantity = item.quantity || 1;
      const extrasTotal = (item.selectedExtras || []).reduce((sum, extra) => sum + (extra.price || 0), 0);
      if (item.dealId) {
        // base price of deal items is 0; extras still count
        total += extrasTotal * quantity;
        const dealId = item.dealId;
        if (!dealCounts[dealId]) dealCounts[dealId] = { pizzas: 0, drinks: 0 };
        const isPizza = pizzaCats.includes(item.category);
        const isDrink = item.category === 'drinks';
        if (isPizza) dealCounts[dealId].pizzas += quantity;
        if (isDrink) dealCounts[dealId].drinks += quantity;
      } else {
        const itemPrice = item.price || 0;
        total += (itemPrice + extrasTotal) * quantity;
      }
    });
    Object.keys(dealCounts).forEach(id => {
      const sets = Math.min(Math.floor(dealCounts[id].pizzas / 2), Math.floor(dealCounts[id].drinks / 1));
      const dealPrice = priceMap[id] || 0;
      total += sets * dealPrice;
    });
    return total;
  };

  const generateWhatsAppMessage = (cartItems, location, deliveryType = 'Lieferung', deliveryFee = 0, name = '', paymentMethod = 'cash') => {
    const now = new Date();
    const deliveryTime = new Date(now.getTime() + 45 * 60000); // +45 mins

    const formatDate = (date) => {
      // 31. Dezember
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'long' });
    };
    const formatTime = (date) => {
      // 23:26
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    };

    const pizzaCats = ['pizzaKlassiker', 'donerPizza', 'mitKaserand', 'mitFischbelag', 'vegetarischePizza'];
    const priceMap = { 1: 15.00, 2: 30.00 };
    const dealCounts = {};
    let subtotal = 0;
    let totalExtrasCost = 0;
    cartItems.forEach(item => {
      const quantity = item.quantity || 1;
      const extrasTotal = (item.selectedExtras || []).reduce((sum, extra) => sum + (extra.price || 0), 0);
      totalExtrasCost += extrasTotal * quantity;
      if (item.dealId) {
        // subtotal += extrasTotal * quantity; // Removed: extras are now separate
        const dealId = item.dealId;
        if (!dealCounts[dealId]) dealCounts[dealId] = { pizzas: 0, drinks: 0 };
        const isPizza = pizzaCats.includes(item.category);
        const isDrink = item.category === 'drinks';
        if (isPizza) dealCounts[dealId].pizzas += quantity;
        if (isDrink) dealCounts[dealId].drinks += quantity;
      } else {
        let itemPrice = item.price || 0;
        // Use item.prices.sonder existence as the pizza check (category is stored as a label, not a key)
        if (item.selectedSizeKey === 'normal' && (deliveryType === 'Abholung' || deliveryType === 'pickup')) {
          if (item.prices && item.prices.sonder !== undefined) {
            itemPrice = item.prices.sonder;
          }
        }
        subtotal += itemPrice * quantity;
      }
    });
    Object.keys(dealCounts).forEach(id => {
      const sets = Math.min(Math.floor(dealCounts[id].pizzas / 2), Math.floor(dealCounts[id].drinks / 1));
      const dealPrice = priceMap[id] || 0;
      subtotal += sets * dealPrice;
    });
    const total = subtotal + totalExtrasCost + deliveryFee;
    const paymentLabel = paymentMethod === 'paypal' ? 'PayPal' : 'Bar';

    // Group items by category
    const groupedItems = cartItems.filter(i => !i.dealId).reduce((acc, item) => {
      const category = item.category || 'Sonstiges';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});

    // Helper to pad string to specific length
    const padRight = (str, length) => {
      if (str.length >= length) return str;
      return str + ' '.repeat(length - str.length);
    };

    let message = `            (Amir's Pizza)\n`;
    message += `          ${deliveryType === 'Lieferung' ? '(Lieferung)' : '(Abholung)'} \n`;
    message += `     Fällig: ${formatDate(deliveryTime)}, um ${formatTime(deliveryTime)} \n`;
    message += ` ------------------------------------------------------------------------------------------ \n`;

    const getItemAnnotation = (item) => {
      if (deliveryType === 'Lieferung') return '(Lieferung)';
      if (item.dealId === 1) return '(PAAR DEAL)';
      if (item.dealId === 2) return '(FREUNDE & FAMILIEN DEAL)';
      const sz = (item.selectedSize || '').toLowerCase();
      if (item.pickupOnly || sz.includes('sonder')) return '(SONDER ANGEBOT)';
      return '(Abholung)';
    };

    Object.keys(groupedItems).forEach(category => {
      message += ` [${category.toUpperCase()}] \n`;
      groupedItems[category].forEach(item => {
        message += ` ------------------------------------------------ \n`;
        const quantity = item.quantity || 1;
        let itemPrice = item.price || 0;

        // Use item.prices.sonder existence as the pizza check (category is stored as a label, not a key)
        if (item.selectedSizeKey === 'normal' && (deliveryType === 'Abholung' || deliveryType === 'pickup')) {
          if (item.prices && item.prices.sonder !== undefined) {
            itemPrice = item.prices.sonder;
          }
        }

        const extrasTotal = (item.selectedExtras || []).reduce((sum, extra) => sum + (extra.price || 0), 0);
        const singleItemTotal = itemPrice + extrasTotal;
        const totalItemPrice = singleItemTotal * quantity;
        const itemIdentifier = item.code ? `${item.code} ` : '';

        // Construct the main line: "3 x 005 Salami - Champignons"
        // Update getItemAnnotation
        const getItemAnnotationOverride = (item) => {
          if (deliveryType === 'Lieferung' || deliveryType === 'delivery') return '(Lieferung)';
          if (item.dealId === 1) return '(PAAR DEAL)';
          if (item.dealId === 2) return '(FREUNDE & FAMILIEN DEAL)';
          const sz = (item.selectedSize || '').toLowerCase();
          const isPizza = pizzaCats.includes(item.category);
          const isPickupSonder = (isPizza && item.selectedSizeKey === 'normal' && (deliveryType === 'Abholung' || deliveryType === 'pickup'));
          if (item.pickupOnly || sz.includes('sonder') || isPickupSonder) return '(SONDER ANGEBOT)';
          return '(Abholung)';
        };

        const itemLabel = getItemAnnotationOverride(item);
        const mainLineStart = ` ${quantity} x ${itemIdentifier}${item.name} • ${itemLabel}`;
        const priceStr = `[${totalItemPrice.toFixed(2)}€]`;
        // Calculate needed padding (aiming for alignment around character 50-60)
        // Fixed width for the name part
        const namePadding = 50;
        let paddedName = mainLineStart;
        if (mainLineStart.length < namePadding) {
          paddedName = mainLineStart + ' '.repeat(namePadding - mainLineStart.length);
        }
        message += `${paddedName}${priceStr}\n`;

        if (item.selectedSize) {
          message += ` Größe: ${item.selectedSize}\n`;
        }
        if (item.selectedExtras && item.selectedExtras.length > 0) {
          message += ` Extras: ${item.selectedExtras.map(e => e.name).join(', ')}\n`;
        }
      });
      message += `\n`;
    });

    const dealLabels = { 1: 'PAAR DEAL', 2: 'FREUNDE & FAMILIEN DEAL' };
    const dealItemsMap = {};
    cartItems.forEach(it => {
      if (it.dealId) {
        const id = Number(it.dealId);
        if (!dealItemsMap[id]) dealItemsMap[id] = [];
        dealItemsMap[id].push(it);
      }
    });

    Object.keys(dealCounts).forEach(idStr => {
      const dealId = Number(idStr);
      const sets = Math.min(Math.floor(dealCounts[dealId].pizzas / 2), Math.floor(dealCounts[dealId].drinks / 1));
      if (sets <= 0) return;

      const dealLabel = dealLabels[dealId] || 'DEAL';
      message += ` [${dealLabel}] \n`;

      const items = dealItemsMap[dealId] || [];
      const namePadding = 50;
      items.forEach(item => {
        message += ` ------------------------------------------------ \n`;
        const line = `  ${item.quantity || 1} x ${item.name}`;
        const priceStr = `[0.00€]`;
        let padded = line;
        if (line.length < namePadding) {
          padded = line + ' '.repeat(namePadding - line.length);
        }
        message += `${padded}${priceStr}\n`;

        if (item.selectedSize) {
          message += ` Größe: ${item.selectedSize}\n`;
        }

        if (item.selectedExtras && item.selectedExtras.length > 0) {
          message += ` Extras: ${item.selectedExtras.map(e => e.name).join(', ')}\n`;
        }
      });
      message += ` ------------------------------------------------ \n`;
      const totalDealPrice = (priceMap[dealId] || 0) * sets;
      const summaryLine = ` Total Angebot Price: ${totalDealPrice.toFixed(2)}€`;
      let paddedSummary = summaryLine;
      if (summaryLine.length < namePadding) {
        paddedSummary = summaryLine + ' '.repeat(namePadding - summaryLine.length);
      }
      message += `${paddedSummary}[${totalDealPrice.toFixed(2)}€]\n`;
      message += `\n`;
    });
    message += ` Zwischensumme                                           [${subtotal.toFixed(2)}€]\n`;
    if (totalExtrasCost > 0) {
      message += ` Extras                                                  [${totalExtrasCost.toFixed(2)}€]\n`;
    }
    message += ` Gesamtbetrag der Bestellung                                [${total.toFixed(2)}€]\n`;
    if (deliveryFee > 0) {
      message += ` Liefergebühr                                         [${deliveryFee.toFixed(2)}€]\n`;
    }
    message += ` ------------------------------------------------------------------------------------------ \n`;
    message += ` Gesamtbetrag                                               [${total.toFixed(2)}€]\n`;

    message += ` Bezahlt von: \n`;
    message += ` ${paymentLabel} ************ [${total.toFixed(2)}€]\n`;
    message += ` ------------------------------------------------------------------------------------------ \n`;
    message += ` ${paymentMethod === 'paypal' ? 'BESTELLUNG IST BEZAHLT (PayPal)' : 'BEZAHLUNG BEI LIEFERUNG (Bar)'} \n`;
    message += ` ------------------------------------------------------------------------------------------ \n`;
    message += ` Details zum/zur Kund:in: \n`;
    message += ` Name: ${name}\n`;
    if (deliveryType === 'Lieferung' && location) {
      message += ` Adresse: ${location}\n`;
    }
    message += ` ------------------------------------------------------------------------------------------ \n`;
    message += ` Bestellung aufgegeben um  [${formatTime(now)}] [${formatDate(now)}]\n`;

    return message;
  };

  return (
    <CartContext.Provider value={{
      cart,
      orders,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateSize,
      clearCart,
      checkout,
      calculateTotal,
    }}>
      {children}
    </CartContext.Provider>
  );
};
